import React, { Fragment, useMemo, useState } from 'react';
import moment from 'moment';
import { Table, Button, Tooltip, Badge, Card, Row, Col, Icon, Dropdown, Menu } from 'antd';

const tableStyles = `
@keyframes pil-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-4px); }
}
@keyframes pil-beat {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.22); }
}
@keyframes pil-bounce {
  0%, 100% { transform: translateY(0) scaleY(1); }
  40%       { transform: translateY(-5px) scaleY(1.05); }
  70%       { transform: translateY(2px) scaleY(0.96); }
}
@keyframes pil-shake {
  0%, 100% { transform: rotate(0deg); }
  20%       { transform: rotate(-14deg); }
  40%       { transform: rotate(14deg); }
  60%       { transform: rotate(-8deg); }
  80%       { transform: rotate(8deg); }
}
@keyframes pil-glow {
  0%, 100% { box-shadow: 0 0 0 0 var(--gc, rgba(24,144,255,0.35)); }
  50%       { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
}
@keyframes pil-spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(180deg); }
}
@keyframes spin-refresh {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.pil-float  { animation: pil-float  3s   ease-in-out infinite; }
.pil-beat   { animation: pil-beat   1.8s ease-in-out infinite; }
.pil-bounce { animation: pil-bounce 2.2s ease-in-out infinite; }
.pil-shake  { animation: pil-shake  2.6s ease-in-out infinite; }
.pil-glow   { animation: pil-glow   2.4s ease-in-out infinite; }

.pil-row-stripe td { background: #f7f9ff !important; }

/* Status badges */
.pil-status-open {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700;
  background: #f6ffed; border: 1px solid #b7eb8f; color: #389e0d;
}
.pil-status-open .pil-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #52c41a; box-shadow: 0 0 5px #52c41a;
}
.pil-status-warning {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700;
  background: #fff1f0; border: 1px solid #ffa39e; color: #cf1322;
}
.pil-status-warning .pil-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #ff4d4f; box-shadow: 0 0 5px #ff4d4f;
}
.pil-status-served {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700;
  background: #e6fffb; border: 1px solid #87e8de; color: #08979c;
}
.pil-status-served .pil-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #13c2c2; box-shadow: 0 0 5px #13c2c2;
}

/* Action buttons */
.pil-action-btn {
  width: 28px !important; height: 28px !important; min-width: 28px !important;
  padding: 0 !important; border-radius: 7px !important;
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
  font-size: 13px !important;
  border: 1.5px solid #d6e8ff !important;
  background: #e6f7ff !important; color: #1890ff !important;
  transition: all 0.18s !important;
}
.pil-action-btn:hover {
  background: #1890ff !important; color: #fff !important;
  border-color: #1890ff !important;
  box-shadow: 0 3px 10px rgba(24,144,255,0.35) !important;
  transform: translateY(-1px) !important;
}

/* Settings gear dropdown */
.pil-settings-btn {
  width: 28px !important; height: 28px !important; min-width: 28px !important;
  padding: 0 !important; border-radius: 7px !important;
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
  font-size: 13px !important;
  border: 1.5px solid #d6e8ff !important;
  background: #e6f7ff !important; color: #1890ff !important;
  transition: all 0.18s !important;
}
.pil-settings-btn.open {
  background: #1890ff !important; color: #fff !important;
  border-color: #1890ff !important;
  box-shadow: 0 3px 10px rgba(24,144,255,0.35) !important;
}
.pil-settings-btn.open .anticon-setting {
  animation: pil-spin 0.4s cubic-bezier(.22,1,.36,1) forwards !important;
}

/* Dropdown menu */
.pil-action-menu.ant-dropdown-menu {
  border-radius: 10px !important;
  border: 1px solid #e6f0ff !important;
  box-shadow: 0 8px 28px rgba(24,144,255,0.12), 0 2px 8px rgba(0,0,0,0.08) !important;
  padding: 5px !important; min-width: 160px !important;
}
.pil-action-menu .ant-dropdown-menu-item {
  border-radius: 7px !important; padding: 8px 12px !important;
  font-size: 12px !important; font-weight: 600 !important;
  transition: background 0.15s, transform 0.12s !important;
}
.pil-action-menu .ant-dropdown-menu-item:hover { transform: translateX(3px) !important; }
.pil-menu-blue:hover  { background: #e6f7ff !important; color: #1890ff !important; }
.pil-menu-green:hover { background: #f6ffed !important; color: #52c41a !important; }
.pil-menu-purple:hover { background: #f9f0ff !important; color: #722ed1 !important; }

/* Delivered/Qty split */
.pil-qty-wrap {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 12px; font-weight: 600;
}
.pil-qty-delivered { color: #52c41a; }
.pil-qty-sep { color: #d9d9d9; }
.pil-qty-total { color: #595959; }

/* Date cell */
.pil-date-warn { color: #cf1322; font-weight: 700; }
.pil-date-ok   { color: #595959; }

/* Enhanced Button UI Design - Same as PoList */
.action-button {
  height: 36px !important;
  padding: 0 20px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.action-button:active {
  transform: translateY(0px);
}

.btn-export {
  background: #fff !important;
  border: 1.5px solid #d9d9d9 !important;
  color: #595959 !important;
}

.btn-export:hover {
  border-color: #52c41a !important;
  color: #52c41a !important;
  background: #f6ffed !important;
}

.btn-refresh {
  background: #fff !important;
  border: 1.5px solid #d9d9d9 !important;
  color: #595959 !important;
}

.btn-refresh:hover {
  border-color: #1890ff !important;
  color: #1890ff !important;
  background: #e6f7ff !important;
}

.action-button .anticon {
  font-size: 14px;
  transition: transform 0.2s ease;
}

.action-button:hover .anticon {
  transform: scale(1.05);
}

.btn-refresh:hover .anticon-reload {
  animation: spin-refresh 0.6s ease-in-out;
}
`;

const ActionCell = ({ data, setItemDeliveryDetails, setItemDeliverySchedule, setOverallDetails }) => {
  const [open, setOpen] = useState(false);

  const menu = (
    <Menu className="pil-action-menu">
      <Menu.Item
        key="delivery"
        className="pil-menu-blue"
        onClick={() => setItemDeliveryDetails({ visible: true, id: data.id })}
      >
        <Icon type="carry-out" style={{ marginRight: 8, color: '#1890ff' }} />
        Add / View Deliveries
        {data.hasDelivery > 0 && (
          <Badge count={data.hasDelivery} style={{ marginLeft: 6, backgroundColor: '#1890ff', fontSize: 10 }} />
        )}
      </Menu.Item>
      <Menu.Item
        key="schedule"
        className="pil-menu-green"
        onClick={() => setItemDeliverySchedule({ visible: true, id: data.id })}
      >
        <Icon type="calendar" style={{ marginRight: 8, color: '#52c41a' }} />
        View Schedule
        {data.hasSchedule > 0 && (
          <Badge count={data.hasSchedule} style={{ marginLeft: 6, backgroundColor: '#52c41a', fontSize: 10 }} />
        )}
      </Menu.Item>
      <Menu.Item
        key="overview"
        className="pil-menu-purple"
        onClick={() => setOverallDetails({ visible: true, id: data.id, item: data.itemdesc, po: data.po_num })}
      >
        <Icon type="book" style={{ marginRight: 8, color: '#722ed1' }} />
        View Item Progress
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['hover']}
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      onVisibleChange={v => setOpen(v)}
    >
      <Button
        className={`pil-settings-btn${open ? ' open' : ''}`}
        size="small"
        icon="setting"
      />
    </Dropdown>
  );
};

function PoItemList(props) {
  const {
    poItems,
    setItemDeliverySchedule,
    setItemDeliveryDetails,
    customers,
    setOverallDetails,
    pageLength,
    paginate,
    onPageChange,
    onSizeChange,
    onExportCSV,
    onRefresh,
  } = props;

  const stats = useMemo(() => {
    if (!poItems || poItems.length === 0) return { total: 0, open: 0, served: 0, warning: 0 };
    const open    = poItems.filter(p => p.status === 'OPEN').length;
    const served  = poItems.filter(p => p.status === 'SERVED').length;
    const warning = poItems.filter(p => {
      const diff = moment(p.deliverydate).diff(moment(), 'days');
      return diff < 3 && p.status === 'OPEN';
    }).length;
    return { total: poItems.length, open, served, warning };
  }, [poItems]);

  const cardStyle = {
    borderRadius: 10,
    boxShadow: '0 2px 12px rgba(24,144,255,0.08)',
    border: '1px solid #e6f0ff',
    background: '#fff',
    height: '100%',
  };

  const statCards = [
    { title: 'Total Items', value: stats.total,   icon: 'unordered-list', color: '#1890ff', bg: '#e6f7ff', anim: 'pil-float',  gc: 'rgba(24,144,255,0.3)' },
    { title: 'Open',        value: stats.open,    icon: 'folder-open',    color: '#52c41a', bg: '#f6ffed', anim: 'pil-bounce', gc: 'rgba(82,196,26,0.3)'  },
    { title: 'Served',      value: stats.served,  icon: 'check-circle',   color: '#13c2c2', bg: '#e6fffb', anim: 'pil-beat',   gc: 'rgba(19,194,194,0.3)' },
    { title: 'Due Soon',    value: stats.warning, icon: 'warning',        color: '#ff4d4f', bg: '#fff1f0', anim: 'pil-shake',  gc: 'rgba(255,77,79,0.3)'  },
  ];

  const tableColumns = [
    {
      key: 'actions',
      title: '',
      align: 'center',
      width: 46,
      render: data => (
        <ActionCell
          data={data}
          setItemDeliveryDetails={setItemDeliveryDetails}
          setItemDeliverySchedule={setItemDeliverySchedule}
          setOverallDetails={setOverallDetails}
        />
      ),
    },
    {
      key: 'status',
      title: 'STATUS',
      align: 'center',
      width: 100,
      render: data => {
        const diff = moment(data.deliverydate).diff(moment(), 'days');
        const hasWarning = diff < 3 && data.status === 'OPEN';
        if (data.status === 'SERVED') {
          return <span className="pil-status-served"><span className="pil-dot" />SERVED</span>;
        }
        if (hasWarning) {
          return <span className="pil-status-warning"><span className="pil-dot" />OPEN</span>;
        }
        return <span className="pil-status-open"><span className="pil-dot" />OPEN</span>;
      },
      filters: [{ text: 'OPEN', value: 'OPEN' }, { text: 'SERVED', value: 'SERVED' }],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
    },
    {
      key: 'customer',
      title: 'CUSTOMER',
      dataIndex: 'customer',
      filters: customers.map(c => ({ text: c.companyname, value: c.companyname })),
      onFilter: (value, record) => record.customer.indexOf(value) === 0,
      sorter: (a, b) => (a.customer || '').localeCompare(b.customer || ''),
    },
    {
      key: 'po_num',
      title: 'PO NO.',
      dataIndex: 'po_num',
      sorter: (a, b) => (a.po_num || '').localeCompare(b.po_num || ''),
    },
    {
      key: 'code',
      title: 'CODE',
      dataIndex: 'code',
      sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
    },
    {
      key: 'partnum',
      title: 'PART NO.',
      dataIndex: 'partnum',
      sorter: (a, b) => (a.partnum || '').localeCompare(b.partnum || ''),
    },
    {
      key: 'itemdesc',
      title: 'ITEM DESCRIPTION',
      dataIndex: 'itemdesc',
      sorter: (a, b) => (a.itemdesc || '').localeCompare(b.itemdesc || ''),
    },
    {
      key: 'unit',
      title: 'UNIT',
      dataIndex: 'unit',
      align: 'center',
      sorter: (a, b) => (a.unit || '').localeCompare(b.unit || ''),
    },
    {
      key: 'quantity',
      title: 'DELIVERED / QTY',
      align: 'center',
      render: data => (
        <span className="pil-qty-wrap">
          <span className="pil-qty-delivered">{data.delivered_qty}</span>
          <span className="pil-qty-sep"> / </span>
          <span className="pil-qty-total">{data.quantity}</span>
        </span>
      ),
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      key: 'unitprice',
      title: 'UNIT PRICE',
      dataIndex: 'unitprice',
      align: 'center',
      sorter: (a, b) => a.unitprice - b.unitprice,
      render: val => val
        ? <span style={{ fontSize: 12, fontWeight: 600, color: '#262626' }}>{val}</span>
        : <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />,
    },
    {
      key: 'deliverydate',
      title: 'DEL. DATE',
      dataIndex: 'deliverydate',
      filters: [
        { text: '7 DAYS',  value: 7  },
        { text: '15 DAYS', value: 15 },
        { text: '30 DAYS', value: 30 },
      ],
      onFilter: (value, record) => moment(record.deliverydate).diff(moment(), 'days') < value,
      sorter: (a, b) => moment(a.deliverydate || '').unix() - moment(b.deliverydate || '').unix(),
      render: (val, record) => {
        const diff = moment(val).diff(moment(), 'days');
        const isWarn = diff < 3 && record.status === 'OPEN';
        return (
          <span className={isWarn ? 'pil-date-warn' : 'pil-date-ok'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <Icon type={isWarn ? 'warning' : 'calendar'} style={{ fontSize: 11 }} />
            {val}
          </span>
        );
      },
    },
    {
      key: 'remarks',
      title: 'REMARKS',
      dataIndex: 'remarks',
      sorter: (a, b) => (a.remarks || '').localeCompare(b.remarks || ''),
      render: val => val || <Icon type="ellipsis" style={{ color: '#d9d9d9' }} />,
    },
  ];

  return (
    <Fragment>
      <style>{tableStyles}</style>

      {/* Stat Cards */}
      <br></br>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((card, idx) => (
          <Col key={idx} xs={12} sm={6}>
            <Card style={cardStyle} bodyStyle={{ padding: '18px 20px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  className={`pil-glow ${card.anim}`}
                  style={{
                    width: 46, height: 46, borderRadius: 12, background: card.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, '--gc': card.gc,
                    animation: `pil-glow 2.4s ease-in-out infinite`,
                  }}
                >
                  <Icon type={card.icon} style={{ fontSize: 20, color: card.color }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 500, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                    {card.title.toUpperCase()}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: card.color, lineHeight: 1.2 }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* BUTTONS SECTION - RIGHT SIDE with UI Design */}
      <div style={{ 
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        {onExportCSV && (
          <Button
            className="action-button btn-export"
            onClick={onExportCSV}
            icon="export"
          >
            Export to CSV
          </Button>
        )}
        
        {onRefresh && (
          <Button
            className="action-button btn-refresh"
            onClick={onRefresh}
            icon="reload"
          >
            Refresh Content
          </Button>
        )}
      </div>

      {/* Table Card */}
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 2px 12px rgba(24,144,255,0.07)', border: '1px solid #e6f0ff' }}
        bodyStyle={{ padding: '0 0 8px 0' }}
      >
        <div style={{
          padding: '14px 20px 12px', borderBottom: '1px solid #f0f5ff',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Icon type="file-done" className="pil-beat" style={{ color: '#1890ff', fontSize: 16 }} />
          <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '0.5px' }}>
            PO ITEMS
          </span>
          <Badge
            count={stats.total}
            style={{ backgroundColor: '#1890ff', fontSize: 11, fontWeight: 600 }}
            overflowCount={99999}
          />
          {stats.warning > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 10, marginLeft: 4,
              background: '#fff1f0', border: '1px solid #ffa39e',
              fontSize: 11, fontWeight: 700, color: '#cf1322',
            }}>
              <Icon type="warning" style={{ fontSize: 10 }} />
              {stats.warning} DUE SOON
            </span>
          )}
        </div>

        <Table
          bordered={true}
          size="small"
          dataSource={poItems}
          columns={tableColumns}
          rowKey="id"
          bodyStyle={{ overflowX: 'auto' }}
          style={{ margin: 0 }}
          rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'pil-row-stripe'}
          pagination={{
            total: pageLength,
            current: paginate.page,
            pageSize: paginate.pageSize,
            defaultPageSize: paginate.pageSize,
            showSizeChanger: true,
            onChange: onPageChange,
            onShowSizeChange: onSizeChange,
            pageSizeOptions: ['10', '15', '25'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
            position: 'both',
            style: { padding: '8px 16px', marginBottom: 0 },
          }}
        />
      </Card>
    </Fragment>
  );
}

export default PoItemList;